import { useState, useRef } from "react";
import { useForm } from "react-hook-form";
import { Turnstile } from "@marsidev/react-turnstile";
import { motion } from "framer-motion";
import {
  ShieldCheck,
  Lock,
  CreditCard,
  ArrowRight,
  Loader2,
} from "lucide-react";
import axios from "axios";
import Seo from "@/components/Seo";
import Header from "@/components/landing/Header";
import { useCart } from "@/context/CartContext";
import { formatAmount } from "@/lib/format";
import { emailRules, requireNonEmpty } from "@/lib/validators";
import { resolveApiError } from "@/lib/api-errors";

const API_URL = process.env.REACT_APP_API_URL || "";
const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || "";

const FIELD_CLASS =
  "mt-2 w-full rounded-xl border border-violet-light bg-white px-4 py-3 text-[15px] text-ink shadow-sm outline-none transition-all duration-150 focus:border-violet-fuerte focus:ring-2 focus:ring-violet-fuerte/20 placeholder:text-ink-3";
const LABEL_CLASS = "block text-sm font-semibold text-ink";
const ERROR_CLASS = "mt-1.5 text-[13px] text-red-500";

const TRUST = [
  { Icon: ShieldCheck, text: "Pago seguro con Stripe" },
  { Icon: Lock, text: "Cancela cuando quieras" },
  { Icon: CreditCard, text: "Sin compromiso de permanencia" },
];

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 18 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] },
});

export default function CheckoutPage() {
  const { plan, billing, addons, basePrice, total, annualCharge, pricingLoading } = useCart();
  const [turnstileToken, setTurnstileToken] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const turnstileRef = useRef(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({ mode: "onBlur" });

  const billingLabel = billing === "annual" ? "Anual" : "Mensual";
  const planLabel = plan
    ? plan.charAt(0).toUpperCase() + plan.slice(1)
    : "";

  const onSubmit = async (data) => {
    if (!API_URL) {
      setErrorMessage("El servicio de pago no está disponible en este momento. Inténtalo más tarde.");
      return;
    }
    if (TURNSTILE_SITE_KEY && !turnstileToken) {
      setErrorMessage("Completa la verificación de seguridad antes de continuar.");
      return;
    }
    setLoading(true);
    setErrorMessage("");
    try {
      const payload = {
        plan,
        billing,
        name: data.name.trim(),
        email: data.email.trim().toLowerCase(),
        churchName: data.churchName.trim(),
        city: data.city.trim(),
        addons,
        turnstileToken: turnstileToken || null,
      };
      if (data.phone?.trim()) {
        payload.phone = data.phone.trim();
      }
      const response = await axios.post(
        `${API_URL}/public/billing/checkout`,
        payload
      );
      const redirectUrl = response.data?.checkoutUrl || response.data?.url;
      if (!redirectUrl) {
        throw new Error("No se recibió URL de pago. Inténtalo de nuevo.");
      }
      try {
        const parsed = new URL(redirectUrl);
        const isStripeHost = /^([a-z0-9-]+\.)*stripe\.com$/i.test(parsed.hostname);
        if (parsed.protocol !== "https:" || !isStripeHost) {
          throw new Error("URL de pago inválida.");
        }
      } catch {
        throw new Error("No se recibió URL de pago válida. Inténtalo de nuevo.");
      }
      window.location.href = redirectUrl;
    } catch (err) {
      setErrorMessage(resolveApiError(err));
      setLoading(false);
      turnstileRef.current?.reset();
      setTurnstileToken(null);
    }
  };

  return (
    <div className="flex min-h-screen w-full overflow-hidden">
      <Seo
        title="Checkout"
        description="Completa tu suscripción a Ekklesia de forma segura con Stripe."
        path="/pago"
        noIndex
      />
      <Header />

      {/* LEFT PANEL — dark */}
      <div
        className="relative hidden lg:flex lg:w-[42%] flex-col justify-center px-12 xl:px-16 overflow-hidden"
        style={{
          background:
            "linear-gradient(165deg, #1A1135 0%, #2E2458 52%, #1F1838 100%)",
        }}
      >
        {/* decorative glows */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute left-0 top-0 h-1 w-full"
          style={{
            background:
              "linear-gradient(90deg, transparent, rgba(201,162,39,0.7), transparent)",
          }}
        />
        <div
          aria-hidden="true"
          className="pointer-events-none absolute top-1/4 -left-20 h-[400px] w-[400px] rounded-full opacity-20 blur-[80px]"
          style={{
            background: "radial-gradient(circle, #4A3A8A 0%, transparent 70%)",
          }}
        />
        <div
          aria-hidden="true"
          className="pointer-events-none absolute bottom-1/4 right-0 h-[300px] w-[300px] rounded-full opacity-15 blur-[70px]"
          style={{
            background: "radial-gradient(circle, #C9A227 0%, transparent 70%)",
          }}
        />

        <div className="relative z-10">
          <motion.p
            {...fadeUp(0.05)}
            className="font-display text-xs font-bold uppercase tracking-[0.22em] text-gold"
          >
            Checkout
          </motion.p>

          <motion.h1
            {...fadeUp(0.12)}
            className="mt-4 font-display text-4xl font-black leading-[1.06] tracking-tight text-white xl:text-5xl"
          >
            Completa tu pedido
          </motion.h1>

          {/* Order summary */}
          <motion.div {...fadeUp(0.2)} className="mt-8">
            {!plan ? (
              <div className="rounded-2xl border border-white/10 bg-white/[0.05] px-6 py-5">
                <p className="text-[15px] text-white/55">
                  No hay ningún plan seleccionado.
                </p>
                <a
                  href="/"
                  className="mt-3 inline-block text-sm font-semibold text-gold hover:underline"
                >
                  Ver planes →
                </a>
              </div>
            ) : (
              <div className="rounded-2xl border border-white/10 bg-white/[0.05] px-6 py-5 backdrop-blur-sm">
                {/* Plan row */}
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-[16px] font-bold text-white">
                      Plan {planLabel}
                    </p>
                    <p className="mt-0.5 text-[13px] text-white/45">
                      {billingLabel}
                    </p>
                  </div>
                  <p className="text-[16px] font-bold text-white">
                    {formatAmount(basePrice)} €
                    <span className="text-[13px] font-normal text-white/45">/mes</span>
                  </p>
                </div>

                {/* Divider */}
                <div className="my-4 h-px bg-white/10" aria-hidden="true" />

                {/* Total */}
                {billing === "annual" && annualCharge !== null ? (
                  <div>
                    <div className="flex items-center justify-between">
                      <p className="text-[13px] text-white/45">Equivale a</p>
                      <p className="text-[14px] text-white/65">
                        {formatAmount(total)} €/mes
                      </p>
                    </div>
                    <div className="mt-1 flex items-baseline justify-between">
                      <p className="text-[15px] font-bold text-white/80">
                        Cargo anual
                      </p>
                      <p className="text-[20px] font-black text-gold">
                        {formatAmount(annualCharge)} €
                        <span className="text-[13px] font-normal text-white/45">/año</span>
                      </p>
                    </div>
                    <p className="mt-1 text-[11px] text-white/30">
                      Pago único · 2 meses gratis
                    </p>
                  </div>
                ) : (
                  <div className="flex items-center justify-between">
                    <p className="text-[15px] font-bold text-white/80">Total</p>
                    <p className="text-[20px] font-black text-gold">
                      {formatAmount(total)} €
                      <span className="text-[13px] font-normal text-white/45">/mes</span>
                    </p>
                  </div>
                )}
              </div>
            )}
          </motion.div>

          {/* Trust items */}
          <motion.ul {...fadeUp(0.3)} className="mt-8 space-y-4">
            {TRUST.map(({ Icon, text }) => (
              <li key={text} className="flex items-center gap-3">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-white/[0.07] text-gold">
                  <Icon className="h-[17px] w-[17px]" strokeWidth={1.75} />
                </span>
                <span className="text-[15px] text-white/65">{text}</span>
              </li>
            ))}
          </motion.ul>

          <motion.a
            {...fadeUp(0.38)}
            href="/"
            className="group mt-10 inline-flex items-center gap-2 text-sm font-semibold text-white/30 transition-colors duration-200 hover:text-white/70"
          >
            <ArrowRight className="h-4 w-4 rotate-180 transition-transform duration-200 group-hover:-translate-x-0.5" />
            Volver al inicio
          </motion.a>
        </div>
      </div>

      {/* RIGHT PANEL — light */}
      <div className="flex flex-1 flex-col items-center justify-center bg-white px-4 pb-10 pt-24 sm:px-8 lg:px-12 lg:pt-0 xl:px-16">
        <div className="w-full max-w-lg">
          {/* Mobile heading */}
          <div className="mb-8 lg:hidden">
            <p className="font-display text-xs font-bold uppercase tracking-[0.22em] text-gold">
              Checkout
            </p>
            <h1 className="mt-2 font-display text-3xl font-black tracking-tight text-ink">
              Completa tu pedido
            </h1>
          </div>

          <motion.h2
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            className="mb-6 font-display text-xl font-bold tracking-tight text-ink"
          >
            Tus datos
          </motion.h2>

          <motion.form
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
            className="space-y-5"
            onSubmit={handleSubmit(onSubmit)}
            noValidate
          >
            {/* Nombre + Email */}
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              <div>
                <label htmlFor="checkout-name" className={LABEL_CLASS}>
                  Nombre
                </label>
                <input
                  id="checkout-name"
                  type="text"
                  autoComplete="name"
                  className={FIELD_CLASS}
                  placeholder="Tu nombre"
                  maxLength={150}
                  {...register("name", {
                    required: "El nombre es obligatorio.",
                    maxLength: { value: 150, message: "El nombre no puede superar 150 caracteres." },
                    validate: requireNonEmpty("El nombre no puede estar vacío."),
                  })}
                />
                {errors.name && (
                  <p className={ERROR_CLASS}>{errors.name.message}</p>
                )}
              </div>
              <div>
                <label htmlFor="checkout-email" className={LABEL_CLASS}>
                  Correo electrónico
                </label>
                <input
                  id="checkout-email"
                  type="email"
                  autoComplete="email"
                  className={FIELD_CLASS}
                  placeholder="nombre@iglesia.org"
                  maxLength={200}
                  {...register("email", emailRules())}
                />
                {errors.email && (
                  <p className={ERROR_CLASS}>{errors.email.message}</p>
                )}
              </div>
            </div>

            {/* Iglesia */}
            <div>
              <label htmlFor="checkout-church" className={LABEL_CLASS}>
                Nombre de la iglesia
              </label>
              <input
                id="checkout-church"
                type="text"
                autoComplete="organization"
                className={FIELD_CLASS}
                placeholder="Iglesia Ejemplo"
                maxLength={200}
                {...register("churchName", {
                  required: "El nombre de la iglesia es obligatorio.",
                  maxLength: { value: 200, message: "El nombre no puede superar 200 caracteres." },
                  validate: requireNonEmpty("El nombre no puede estar vacío."),
                })}
              />
              {errors.churchName && (
                <p className={ERROR_CLASS}>{errors.churchName.message}</p>
              )}
            </div>

            {/* Ciudad + Teléfono */}
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              <div>
                <label htmlFor="checkout-city" className={LABEL_CLASS}>
                  Ciudad
                </label>
                <input
                  id="checkout-city"
                  type="text"
                  autoComplete="address-level2"
                  className={FIELD_CLASS}
                  placeholder="Madrid"
                  maxLength={100}
                {...register("city", {
                    required: "La ciudad es obligatoria.",
                    maxLength: { value: 100, message: "Ciudad demasiado larga." },
                    validate: requireNonEmpty("La ciudad no puede estar vacía."),
                  })}
                />
                {errors.city && (
                  <p className={ERROR_CLASS}>{errors.city.message}</p>
                )}
              </div>
              <div>
                <label htmlFor="checkout-phone" className={LABEL_CLASS}>
                  Teléfono{" "}
                  <span className="font-normal text-ink-3">(opcional)</span>
                </label>
                <input
                  id="checkout-phone"
                  type="tel"
                  autoComplete="tel"
                  className={FIELD_CLASS}
                  placeholder="+34 600 000 000"
                  maxLength={30}
                  {...register("phone", {
                    maxLength: { value: 30, message: "Teléfono demasiado largo." },
                    pattern: {
                      value: /^[+\d\s().-]*$/,
                      message: "Introduce un teléfono válido.",
                    },
                  })}
                />
                {errors.phone && (
                  <p className={ERROR_CLASS}>{errors.phone.message}</p>
                )}
              </div>
            </div>

            {/* Turnstile */}
            {TURNSTILE_SITE_KEY && (
              <Turnstile
                ref={turnstileRef}
                siteKey={TURNSTILE_SITE_KEY}
                onSuccess={(token) => setTurnstileToken(token)}
                onExpire={() => setTurnstileToken(null)}
                onError={() => setTurnstileToken(null)}
                options={{ theme: "light", language: "es" }}
              />
            )}

            {/* Error */}
            {errorMessage && (
              <p className="rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-[13px] text-red-600">
                {errorMessage}
              </p>
            )}

            {/* Submit */}
            <button
              type="submit"
              disabled={loading || !plan || pricingLoading}
              className="inline-flex h-[52px] w-full items-center justify-center gap-2 rounded-xl bg-violet-fuerte text-[16px] font-semibold text-white shadow-[0_4px_20px_-4px_rgba(74,58,138,0.45)] transition-all duration-200 hover:bg-violet-oscuro hover:shadow-[0_6px_28px_-4px_rgba(74,58,138,0.55)] hover:-translate-y-px disabled:opacity-60 disabled:translate-y-0 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin" />
                  Redirigiendo…
                </>
              ) : (
                "Pagar con Stripe →"
              )}
            </button>

            <p className="text-center text-[13px] text-ink-3">
              Serás redirigido a Stripe para completar el pago de forma segura.
            </p>
          </motion.form>
        </div>
      </div>
    </div>
  );
}
